import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { BaseApiClient } from '@/services/BaseApiClient'
import { API_ENDPOINTS } from '@/config/apiConstants'
import type { UserListItem } from '@/types/User'
import { useAuthStore } from './auth'
import { Permission } from '@/types/Permissions'
import { userService } from '@/services/UserService'

class PresenceApiClient extends BaseApiClient { }
const presenceClient = new PresenceApiClient()

export const usePresenceStore = defineStore('presence', () => {
    const authStore = useAuthStore()
    const onlineUsersRecord = ref<Record<string, UserListItem>>({})

    const onlineCount = computed(() => Object.keys(onlineUsersRecord.value).length)
    const onlineUsersList = computed(() => Object.values(onlineUsersRecord.value))

    // Check if user has permission
    const hasPermission = computed(() => authStore.hasPermission(Permission.USER_PRESENCE_READ))

    // Initialize list of online users on load
    const fetchInitialPresence = async () => {
        if (!hasPermission.value) return

        try {
            const list = await presenceClient.get<UserListItem[]>(API_ENDPOINTS.PRESENCE.ONLINE_USERS)
            const record: Record<string, UserListItem> = {}
            list.forEach(u => {
                record[u.id] = u
            })
            onlineUsersRecord.value = record
        } catch (error) {
            console.error('Failed to fetch initial presence:', error)
        }
    }

    // Handles incoming presence events from WebSocket
    const handlePresenceEvent = async (userId: string, isOnline: boolean) => {
        if (!hasPermission.value) return

        if (isOnline) {
            // Re-fetch details if user goes online and we don't have them
            if (!onlineUsersRecord.value[userId]) {
                // Skip API call if this is the current user — use auth store data
                if (authStore.user && authStore.user.id === userId) {
                    onlineUsersRecord.value[userId] = authStore.user as unknown as UserListItem
                    return
                }
                try {
                    const userDetail = await userService.getUserById(userId)
                    onlineUsersRecord.value[userId] = userDetail as unknown as UserListItem
                } catch (error) {
                    console.error('Failed to fetch newly online user details', error)
                }
            }
        } else {
            // Remove user from online record
            delete onlineUsersRecord.value[userId]
        }
    }

    const reset = () => {
        onlineUsersRecord.value = {}
    }

    return {
        onlineUsersRecord,
        onlineCount,
        onlineUsersList,
        hasPermission,
        fetchInitialPresence,
        handlePresenceEvent,
        reset
    }
})
